import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Auth } from "shared/@auth/Auth";
import { useLocation } from "react-router-dom";

export function useMeetingsQueries(options = { enableExtras: false }) {
  const { makeAuthedRequest } = Auth.useContainer();

  const [res, setRes] = useState({});

  const {
    data: meetingData,
    status: statusMT,
    error: errorMT,
  } = useQuery(
    ["meetings"],
    () =>
      makeAuthedRequest(`/management-team/meetings/MT/current`).then(
        async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        }
      ),
    { enabled: !!options.enableExtras }
  );

  const { meetings } = useMemo(() => {
    if (meetingData == null) {
      return { meetings: [] };
    } else {
      return { meetings: meetingData };
    }
  }, [meetingData]);

  const {
    data: meetingDataEC,
    status: statusEC,
    error: errorEC,
  } = useQuery(
    ["meetingsEC"],
    () =>
      makeAuthedRequest(`/management-team/meetings/EC/current`).then(
        async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        }
      ),
    { enabled: !!options.enableExtras }
  );

  const { meetingsEC } = useMemo(() => {
    if (meetingDataEC == null) {
      return { meetingsEC: [] };
    } else {
      return { meetingsEC: meetingDataEC };
    }
  }, [meetingDataEC]);

  return {
    meetings,
    meetingsEC,
    res,
    setRes,
    statusMT,
    statusEC,
    errorMT,
    errorEC,
  };
}

export function useMeetingsMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();
  const [res, setRes] = useState({});

  const meetingPost = useMutation(
    ["addMeeting"],
    (meetingObject) =>
      makeAuthedRequest(`/management-team/meeting/add`, {
        method: "POST",
        body: JSON.stringify(meetingObject),
      }).then(async (res) => {
        if (res.ok) {
          var json = await res.json();
          setRes(json);
          return json;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("meetings");
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const meetingPut = useMutation(
    ["editMeeting"],
    (meetingObject) =>
      makeAuthedRequest(`/management-team/meeting/update`, {
        method: "PUT",
        body: JSON.stringify(meetingObject),
      }).then(async (res) => {
        if (res.ok) {
          var json = res.json();
          return json;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("meetings");
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const meetingDelete = useMutation(
    ["deleteMeeting"],
    (deleteMeetingObject) =>
      makeAuthedRequest(`/management-team/meeting/delete`, {
        method: "DELETE",
        body: JSON.stringify(deleteMeetingObject),
      }).then(async (res) => {
        if (res.ok) {
          var json = res.json();
          return json;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    {
      onSettled: () => {
        queryClient.invalidateQueries("meetings");
        queryClient.invalidateQueries("dashboard");
      },
    }
  );

  const meetingCheckInProxyPut = useMutation(
    ["checkin-proxy"],
    async (proxyCheckInObject) =>
      makeAuthedRequest(`/management-team/meeting/proxy-checkin`, {
        method: "PUT",
        body: JSON.stringify(proxyCheckInObject),
      }).then(async (res) => {
        if (res.ok) {
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  const meetingCheckInProxyGuestsPut = useMutation(
    ["checkin-proxy"],
    async (proxyCheckInObject) =>
      makeAuthedRequest(`/management-team/meeting/guest-proxy-checkin`, {
        method: "PUT",
        body: JSON.stringify(proxyCheckInObject),
      }).then(async (res) => {
        if (res.ok) {
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  return {
    meetingPost,
    meetingPut,
    meetingDelete,
    meetingCheckInProxyPut,
    meetingCheckInProxyGuestsPut,
    res,
    setRes,
  };
}

export function useMeetings(options = { enableExtras: false }) {
  const q = useMeetingsQueries(options);
  const m = useMeetingsMutations();
  return {
    meetings: q.meetings,
    meetingsEC: q.meetingsEC,
    meetingPost: m.meetingPost,
    meetingPut: m.meetingPut,
    meetingDelete: m.meetingDelete,
    meetingCheckInProxyPut: m.meetingCheckInProxyPut,
    meetingCheckInProxyGuestsPut: m.meetingCheckInProxyGuestsPut,
    res: m.res,
    setRes: q.setRes || m.setRes,
    statusMT: q.statusMT,
    statusEC: q.statusEC,
    errorMT: q.errorMT,
    errorEC: q.errorEC,
  };
}
